import { useState } from 'react';
import { Form, Input, Select, InputNumber, Button, Card, Typography, Row, Col, TimePicker, message, Steps, Spin, Flex } from 'antd';
import { WhatsAppOutlined, TeamOutlined, CheckCircleOutlined, GiftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import futezapLogo from '../assets/futezap.jpg';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const isMobile = window.innerWidth <= 768 ? true : false;

const SignUp = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [firstStepData, setFirstStepData] = useState({});
  const [phoneExists, setPhoneExists] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliateValid, setAffiliateValid] = useState(null);
  const [affiliateData, setAffiliateData] = useState(null);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const dayOfWeekOptions = [
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' }
  ];

  const validateAffiliateCode = async (code) => {
    if (!code) {
      setAffiliateValid(null);
      setAffiliateData(null);
      return;
    }

    try {
      const response = await api.get(`/affiliates/validate/${code}`);
      if (response.data.valid) {
        setAffiliateValid(true);
        setAffiliateData(response.data.affiliate);
      }
    } catch (error) {
      setAffiliateValid(false);
      setAffiliateData(null);
    }
  };

  const debounceValidateAffiliateCode = ((searchParam) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const tmrDebounce = setTimeout(() => {
      validateAffiliateCode(searchParam);
      setDebounceTimeout(null);
    }, 1500);
    setDebounceTimeout(tmrDebounce);
  });

  const handleValidatePhoneAndPassword = async () => {
    try {
      const values = await form.validateFields(['team_name', 'organizer_whatsapp', 'password', 'confirm_password']);

      // Verificar se telefone já existe
      const checkResponse = await api.post('/signup/check-phone', { organizer_whatsapp: values.organizer_whatsapp });
      const checkData = checkResponse.data;

      if (checkData.exists) {
        setPhoneExists(true);
        message.error('Este número de WhatsApp já está cadastrado. Use outro número.', 5);
        return;
      }

      setPhoneExists(false);

      const { confirm_password, ...stepData } = values;
      // Incluir código de afiliado se válido
      if (affiliateCode && affiliateValid) {
        stepData.affiliate_code = affiliateCode;
      }
      setFirstStepData(stepData);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    if (!await handleValidatePhoneAndPassword()) {
      message.error('Preencha todos os campos obrigatórios');
      setLoading(false);
      return;
    }
    try {
      const response = await api.post('/signup', values);
      const data = response.data;

      if (data.success && data.needs_activation) {
        // Redirecionar para tela de ativação
        window.location.href = `/ativacao?customer_id=${data.customer_id}`;
      } else if (data.success) {
        setCurrentStep(1);
        message.success('Cadastro realizado com sucesso!');
      } else {
        message.error(data.message || 'Erro ao realizar cadastro');
      }
    } catch (error) {
      console.error('Erro:', error);
      message.error('Erro ao realizar cadastro. Tente novamente.');
    }
    setLoading(false);
  };

  const steps = [
    {
      title: 'Informações do Time',
      icon: <TeamOutlined />
    },
    {
      title: 'Concluído',
      icon: <CheckCircleOutlined />
    }
  ];

  if (currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #00B96B 0%, #52c41a 100%)', padding: '20px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', paddingTop: '10vh' }}>
          <Card style={{ textAlign: 'center', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: 24 }}>
              <img src={futezapLogo} alt="Futezap" style={{ height: 60, borderRadius: 8 }} />
            </div>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
            <Title level={2} style={{ color: '#00B96B' }}>Cadastro Realizado!</Title>
            <Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
              Seu time foi cadastrado com sucesso! Em breve você receberá uma mensagem no WhatsApp
              para ativar e sincronizar seu grupo.
            </Paragraph>
            <Paragraph style={{ fontSize: 14, color: '#666' }}>
              Após a ativação, você terá acesso completo à plataforma de gestão do seu time,
              onde poderá editar configurações, adicionar jogadores, gerenciar jogos e muito mais!
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={() => window.location.href = 'https://futezap.com.br'}
              style={{ marginTop: 16 }}
            >
              Voltar ao Site
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #00B96B 0%, #52c41a 100%)', padding: '20px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', paddingTop: '5vh' }}>
        <Card style={{ borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 32 }}>
            <img src={futezapLogo} alt="Futezap" style={{ height: 60, borderRadius: 8, marginBottom: 16 }} />
            <Title level={3} style={{ color: '#00B96B', marginBottom: 6 }}>Junte-se a centenas de times organizados com o Futezap</Title>
            <Text style={{ fontSize: isMobile ? 14 : 16, color: '#666' }}>
              Basta preencher essas informações para começar a organizar sua pelada sem dor de cabeça
            </Text>
          </div>

          {isMobile
            ? <Steps size="small" current={currentStep} items={steps} />
            : <Steps current={currentStep} items={steps} />
          }

          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => {
              const formattedValues = {
                ...firstStepData,
                ...values,
                game_time: values.game_time.format('HH:mm:ss')
              };
              handleSubmit(formattedValues);
            }}
            initialValues={{
              game_time: dayjs('19:00', 'HH:mm'),
              plan: 'basico'
            }}
          >
            {currentStep === 0 && (
              <>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="team_name"
                      label="Nome do Time"
                      rules={[{ required: true, message: 'Nome do time é obrigatório' }]}
                    >
                      <Input placeholder="Ex: Pelada do João" size="large" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="organizer_whatsapp"
                      label={
                        <span>
                          <WhatsAppOutlined style={{ color: '#25D366', marginRight: 4 }} />
                          WhatsApp do Organizador
                        </span>
                      }
                      rules={[
                        { required: true, message: 'WhatsApp é obrigatório' },
                        { pattern: /^\d{10,11}$/, message: 'Digite apenas números (DDD + número)' }
                      ]}
                      validateStatus={phoneExists ? 'error' : undefined}
                      help={phoneExists ? 'Este número já está cadastrado' : undefined}
                    >
                      <Input
                        placeholder="81999887766"
                        size="large"
                        onChange={() => setPhoneExists(false)}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="password"
                      label="Senha"
                      rules={[
                        { required: true, message: 'Senha é obrigatória' },
                        { min: 6, message: 'Senha deve ter pelo menos 6 caracteres' }
                      ]}
                    >
                      <Input.Password placeholder="Digite sua senha" size="large" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="confirm_password"
                      label="Confirmar Senha"
                      dependencies={['password']}
                      rules={[
                        { required: true, message: 'Confirmação de senha é obrigatória' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('As senhas não coincidem'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder="Confirme sua senha" size="large" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="day_of_week"
                      label="Dia da Semana do Jogo"
                      rules={[{ required: true, message: 'Selecione o dia da semana' }]}
                      help="Caso tenha mais de 1 jogo por semana, você poderá adicionar outros dias nas configurações do sistema"
                    >
                      <Select placeholder="Selecione o dia" size="large">
                        {dayOfWeekOptions.map(option => (
                          <Option key={option.value} value={option.value}>{option.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="game_time"
                      label="Horário do Jogo"
                      rules={[{ required: true, message: 'Horário é obrigatório' }]}
                    >
                      <TimePicker format="HH:mm" size="large" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} style={{ marginTop: 16 }}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="players"
                      label="Jogadores de Linha"
                      rules={[{ required: true, message: 'Número de jogadores é obrigatório' }]}
                      help="Total de jogadores para fechar a lista"
                    >
                      <InputNumber min={10} max={30} size="large" style={{ width: '100%' }} placeholder="Ex: 16" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="goalkeepers"
                      label="Goleiros"
                      rules={[{ required: true, message: 'Número de goleiros é obrigatório' }]}
                      help="Total de goleiros para fechar a lista"
                    >
                      <InputNumber min={1} max={4} size="large" style={{ width: '100%' }} placeholder="Ex: 2" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label={
                        <span>
                          <GiftOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                          Código de Afiliado (Opcional)
                        </span>
                      }
                      validateStatus={(affiliateValid === false && affiliateCode) ? 'error' : affiliateValid === true ? 'success' : undefined}
                      help={
                          loading || debounceTimeout !== null
                          ? <Flex align="center" gap="6px"><Spin size="small" />Aguarde pesquisando código...</Flex>
                          : affiliateValid === true && affiliateData
                          ? "✅ Você receberá 50% de desconto na primeira mensalidade após o período trial."
                          : affiliateValid === false && affiliateCode
                          ? "❌ Verifique se o código foi digitado corretamente."
                          : !affiliateCode && "Digite o código de um afiliado para receber 50% de desconto na primeira mensalidade"
                      }
                      >
                      <Input
                        placeholder="Ex: JOAO2024"
                        size="large"
                        value={affiliateCode}
                        onChange={(e) => {
                          const code = e.target.value.toUpperCase();
                          setAffiliateCode(code);
                          if (code.length >= 3) {
                            debounceValidateAffiliateCode(code);
                          } else {
                            setAffiliateValid(null);
                            setAffiliateData(null);
                            if (debounceTimeout) clearTimeout(debounceTimeout);
                            setDebounceTimeout(null);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            <Form.Item>
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={loading}
                  style={{ flex: 1, height: 50, fontSize: 16, fontWeight: 'bold' }}
                >
                  {loading ? 'Cadastrando...' : 'Cadastrar Meu Time'}
                </Button>
              </div>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Text style={{ color: '#666', fontSize: 12 }}>
              Ao cadastrar, você concorda com nossos{' '}
              <a href="https://futezap.com.br/termo-de-uso/" target="_blank" rel="noopener noreferrer" style={{ color: '#00B96B' }}>
                termos de uso
              </a>
              {' '}e{' '}
              <a href="https://futezap.com.br/politica-de-privacidade/" target="_blank" rel="noopener noreferrer" style={{ color: '#00B96B' }}>
                política de privacidade
              </a>
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SignUp;